import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  useContentVersionAnnotations,
  useResolveContentVersionAnnotation,
} from '@opallabs/api'
import type {
  AnnotationLayout,
  ContentVersion,
  ContentVersionBlob,
} from '@opallabs/api'
import { noop } from '@opallabs/lambda'
import {
  Button,
  ButtonIconPosition,
  Icon,
  legacyBoxModel,
} from '@opallabs/lapidary'
import { BackbonePostTranslator, ConnectedPost } from '@opallabs/periodic'
import { AnnotationOverlay } from './AnnotationOverlay'
import {
  deriveTranslatorInputs,
  getServiceSystemName,
  hasHiddenSections,
  isPeriodicRenderable,
} from './deriveTranslatorInputs'
import { FallbackPreviewCard } from './FallbackPreviewCard'
import { FeedbackCard } from './FeedbackCard'
import { areMentionSuggestionsShowing } from './mentionSuggestionRenderer'
import * as styles from './styles'
import { VersionByline } from './VersionByline'
import { VersionDetailsTable } from './VersionDetailsTable'

/**
 * `ConnectedPost` is a loosely typed legacy component (`React.VFC` with no prop
 * contract) and the translated post props are a dynamic bag. We render it through a
 * single typed boundary here rather than casting at the JSX site.
 */
type PeriodicPreviewProps = Record<string, unknown> & {
  readOnly: true
  isSnapshot: boolean
  service: string
  display: string
}
const PeriodicPreview =
  ConnectedPost as unknown as React.FC<PeriodicPreviewProps>

/** Mirrors the "mobile single" read-only card used by the live Periodic preview. */
const PREVIEW_DISPLAY = 'mobile single'

type VersionPreviewProps = {
  blob: ContentVersionBlob
  /**
   * Scale the rendered post down to fit its container's width, mirroring the legacy
   * composer's `_scalePreview`. Compare mode's split panes are too narrow for a
   * full-width post.
   */
  fitToWidth?: boolean
  /**
   * The version whose pin annotations, byline, and feedback list this preview owns.
   * Set only in the single-version view; omitted in compare mode, where the preview
   * is a bare canvas.
   */
  version?: ContentVersion
  /**
   * When set, this pin's popover opens as soon as the version's annotations load —
   * the activity feed deep-links to a single piece of feedback. Ignored when the pin
   * belongs to another version.
   */
  initialAnnotationId?: string | null
  /**
   * Whether the viewer may edit this content. Leaving and resolving feedback needs
   * only view access; labelling a version needs edit.
   */
  canEdit?: boolean
  /**
   * Whether this version is the current content. Only the current version renders
   * live; every other version is a frozen record, so it renders in snapshot mode
   * and content types that fetch live state (a linked SharePoint file's preview,
   * say) show what was captured instead of what the source looks like today.
   */
  isCurrent?: boolean
  /**
   * Still image of the edit this version came from, shown in place of the copy
   * fields when the content type cannot render through the live preview. Kept
   * separate from +version+ so compare panes, which pass no version because they
   * carry no pins, can show it too.
   */
  snapshotUrl?: string | null
  /**
   * Reports comment mode turning on and off. The drawer has to know: Escape
   * steps out of comment mode before it closes the drawer, and only the drawer
   * can tell Radix to leave that press alone.
   */
  onCommentModeChange?: (active: boolean) => void
  onLabelCommit?: (label: string | null) => void
}

type PostDimensions = { width: number; height: number }

export const VersionPreview: React.VFC<VersionPreviewProps> = ({
  blob,
  fitToWidth = false,
  version,
  initialAnnotationId = null,
  canEdit = false,
  isCurrent = false,
  snapshotUrl = null,
  onCommentModeChange = noop,
  onLabelCommit = noop,
}) => {
  const versionId = version?.id
  const renderable = isPeriodicRenderable(blob)
  const { data: annotations = [] } = useContentVersionAnnotations(
    versionId ?? '',
    Boolean(versionId)
  )
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const [openId, setOpenId] = useState<string | null>(null)
  const [draft, setDraft] = useState<{ x: number; y: number } | null>(null)
  const [commentMode, setCommentMode] = useState(false)
  // Snapshots are pruned on their own schedule, so a version can outlive its
  // image; a broken image is worse than the copy fields it replaced.
  const [snapshotBroken, setSnapshotBroken] = useState(false)
  const canvasRef = useRef<HTMLDivElement | null>(null)
  // Held in a ref so a caller passing an inline callback does not restart the
  // effect below on every render.
  const onCommentModeChangeRef = useRef(onCommentModeChange)
  onCommentModeChangeRef.current = onCommentModeChange

  // Content whose sections hide behind carousel navigation is previewed — and
  // pinned — in the expanded layout, where every section is on screen at a fixed
  // offset. The collapsed card would put a section-2 pin over section 1 on the
  // next read.
  const isSectioned = hasHiddenSections(blob)
  // A content type Periodic cannot render is pinned on its snapshot image
  // instead — a different picture from either live layout, so it gets its own
  // coordinate space rather than borrowing 'collapsed'.
  const pinLayout: AnnotationLayout = !renderable
    ? 'snapshot'
    : isSectioned
      ? 'expanded'
      : 'collapsed'
  // Compare mode fits two panes side by side and carries no pins, so it stays on
  // the collapsed card.
  const isExpanded = isSectioned && versionId != null
  // The image the fallback actually shows, if any: without one it falls back to
  // the copy fields, which are no surface to pin on.
  const shownSnapshotUrl = snapshotBroken ? null : snapshotUrl
  // Feedback needs a picture to put a pin on: the live preview, or the snapshot
  // standing in for it. The copy-fields card offers neither.
  const canPin = Boolean(versionId) && (renderable || Boolean(shownSnapshotUrl))
  // Coordinates only mean anything in the layout they were measured in.
  const pins = useMemo(
    () => annotations.filter((annotation) => annotation.layout === pinLayout),
    [annotations, pinLayout]
  )
  // Resolved feedback leaves the canvas for the feedback list's resolved group.
  // Pin numbers are positional, so resolving one renumbers the pins after it.
  const openPins = useMemo(
    () => pins.filter((annotation) => !annotation.resolved),
    [pins]
  )
  const resolvedPins = useMemo(
    () => pins.filter((annotation) => annotation.resolved),
    [pins]
  )

  // The drawer gates Radix's own Escape handling on this, so it has to hear
  // about the mode before the next press arrives.
  useEffect(() => {
    onCommentModeChangeRef.current(commentMode)
  }, [commentMode])

  // Escape steps out one layer at a time: the open popover first, then comment
  // mode, and only then the drawer. Capture phase so the drawer's own Escape
  // handling (which would dismiss the whole drawer) never sees a press meant
  // for a layer inside it.
  useEffect(() => {
    if (!commentMode) return
    const exit = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      // The mention list closes on Escape too, and this listener sees the press
      // first — from the document, in the capture phase, above every layer. Left
      // alone it threw away the draft pin and the comment written against it
      // while someone was half way through picking a name.
      if (areMentionSuggestionsShowing()) return
      // An open popover — a draft's composer, or a pin's comment — is the layer
      // above comment mode, and Radix dismisses the topmost one (which clears a
      // draft through onDismissDraft). Comment mode stays on, so the next pin
      // goes down without turning it back on first.
      if (draft || openId) return
      event.stopPropagation()
      setCommentMode(false)
    }
    document.addEventListener('keydown', exit, true)
    return () => document.removeEventListener('keydown', exit, true)
  }, [commentMode, draft, openId])

  // The post's absolutely-positioned pieces (carousel slides/nav) anchor to the
  // nearest positioned ancestor, and the shroud sizes that ancestor from the
  // dimensions ConnectedPost reports through onDisplayChange. Mirror both here.
  const [dimensions, setDimensions] = useState<PostDimensions | null>(null)
  const [availableWidth, setAvailableWidth] = useState<number | null>(null)

  // Selecting from the feedback list (which sits below the fold on tall
  // content) brings the preview — and the opened pin — back into view.
  const selectFromList = useCallback((id: string) => {
    setOpenId(id)
    canvasRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  const { mutate: setAnnotationResolved } = useResolveContentVersionAnnotation(
    versionId ?? ''
  )

  // A resolved pin has no popover to stay open in, so close it on the way out.
  const setResolved = useCallback(
    (id: string, resolved: boolean) => {
      setOpenId((current) => (current === id ? null : current))
      setAnnotationResolved({ id, resolved })
    },
    [setAnnotationResolved]
  )

  // Open the deep-linked pin once its annotations have arrived. This preview
  // remounts per version, so the ref scopes "once" to the version in view and
  // never fights the user reopening pins by hand.
  const hasOpenedDeepLink = useRef(false)
  const [highlightedId, setHighlightedId] = useState<string | null>(null)
  useEffect(() => {
    if (hasOpenedDeepLink.current || initialAnnotationId == null) return
    if (!annotations.some(({ id }) => id === initialAnnotationId)) return
    hasOpenedDeepLink.current = true
    // A popover needs a pin on the canvas to hang off, and only open feedback
    // in this layout has one. Resolved feedback has a row in the list instead,
    // so point at that; feedback measured in the layout this preview isn't
    // showing has neither, and there is nothing to point at.
    if (openPins.some(({ id }) => id === initialAnnotationId))
      selectFromList(initialAnnotationId)
    else if (resolvedPins.some(({ id }) => id === initialAnnotationId))
      setHighlightedId(initialAnnotationId)
  }, [annotations, openPins, resolvedPins, initialAnnotationId, selectFromList])

  // The expanded layout is wider than most panes, so it needs measuring against
  // the space available and scaling down when it doesn't fit.
  const shouldFitToWidth = fitToWidth || isExpanded

  // A compare pane holds the collapsed card, which is fluid: it takes its width
  // from the pane. It must not be sized from its own content — one oversized
  // image makes the frame as wide as that image's natural size, and the scale
  // that fits the frame back into the pane then shrinks the rest of the card to
  // hairlines.
  const isFluidPane = fitToWidth && !isExpanded

  // What the card actually paints at, which in a fluid pane is not what the post
  // reports. The pane squeezes the frame to its own width and Periodic's cards
  // carry widths of their own (484 for a collapsed carousel), so they overflow it
  // silently while the ResizeObserver behind `onDisplayChange` only ever sees the
  // squeezed box — leaving the scale below with nothing to do and the overflow to
  // `scaleClip`, which cuts the carousel's next arrow off.
  //
  // Measured off the card rather than the post's `scrollWidth` on purpose. A card
  // that really is fluid reports the pane's width and stays unscaled, and an
  // oversized image inside a card cannot inflate a width the content type
  // declared — which is what made sizing the frame from its content untenable.
  // `offsetWidth`, not `getBoundingClientRect`, so the scale we apply below is not
  // fed back in on the next pass.
  const frameRef = useRef<HTMLDivElement | null>(null)
  const [paintedWidth, setPaintedWidth] = useState<number | null>(null)
  useLayoutEffect(() => {
    if (!isFluidPane) {
      setPaintedWidth(null)
      return
    }
    const post = frameRef.current?.querySelector<HTMLElement>('.kuiper-post')
    if (!post) return
    const widest = Math.max(
      0,
      ...Array.from(post.children, (card) => (card as HTMLElement).offsetWidth)
    )
    setPaintedWidth(widest || null)
  }, [isFluidPane, dimensions, availableWidth])

  const observerRef = useRef<ResizeObserver | null>(null)
  const measureRef = useCallback(
    (node: HTMLDivElement | null) => {
      canvasRef.current = node
      observerRef.current?.disconnect()
      if (!shouldFitToWidth || !node) return
      const observer = new ResizeObserver(([entry]) => {
        const width = entry.contentRect.width
        if (width) setAvailableWidth(width)
      })
      observer.observe(node)
      observerRef.current = observer
    },
    [shouldFitToWidth]
  )

  const periodicProps = useMemo(() => {
    if (!renderable) return null
    const translator = new BackbonePostTranslator(deriveTranslatorInputs(blob))
    return translator.props as Record<string, unknown>
  }, [blob, renderable])

  // Only shrink; never enlarge a post past the width it paints at.
  const paintedOrReported = paintedWidth ?? dimensions?.width
  const scale =
    paintedOrReported && availableWidth && paintedOrReported > availableWidth
      ? availableWidth / paintedOrReported
      : 1
  const isScaled = scale < 1

  const frameStyle = isFluidPane
    ? // Follow the pane. Pinned to a measured width, the card would stay at its
      // old size when the pane grew. A card too wide for the pane is scaled down
      // to fit rather than clipped; the frame keeps the pane's width, so the
      // scaled card lands flush inside it.
      isScaled
      ? {
          inlineSize: '100%',
          transform: `scale(${scale})`,
          transformOrigin: 'left top',
        }
      : { inlineSize: '100%' }
    : dimensions && isScaled
      ? {
          ...dimensions,
          // The frame keeps the post's own width so the pin overlay (inset 0)
          // covers exactly the post; the transform fits it into the pane.
          transform: `scale(${scale})`,
          transformOrigin: 'left top',
        }
      : (dimensions ?? undefined)

  const frame = periodicProps ? (
    <div
      ref={frameRef}
      css={[
        styles.periodicFrame,
        isFluidPane && styles.fluidFrame,
        isExpanded && styles.intrinsicFrame,
        isExpanded && styles.expandedSectionSpacing,
      ]}
      style={frameStyle}
      // The content's own bounds, which `annotationPin` hangs a `:hover` rule
      // off to un-dim the pins while the cursor is over the content.
      data-preview-content
    >
      {/* Periodic's content types are written against the browser's own box
          model, and the drawer hosting this preview resets its whole subtree to
          `border-box`. This marks the post as foreign so the reset skips it, and
          every element inside falls back to its own box-sizing or the default —
          the environment the content renders in everywhere else. Only the post
          goes inside: the pin overlay below is ours and is sized `border-box`.
          Carries no `position`, so the post's absolutely-positioned pieces still
          anchor to the frame. */}
      <div {...legacyBoxModel}>
        <PeriodicPreview
          {...periodicProps}
          service={getServiceSystemName(blob)}
          display={PREVIEW_DISPLAY}
          readOnly
          isSnapshot={!isCurrent}
          // Read-only contexts default to the collapsed card, which shows one
          // section at a time. Sectioned content is previewed expanded instead so
          // every section — and every pin — is on screen.
          isExpanded={isExpanded}
          onDisplayChange={(next?: PostDimensions) => {
            if (!next?.width || !next?.height) return
            setDimensions((prev) =>
              prev?.width === next.width && prev?.height === next.height
                ? prev
                : { width: next.width, height: next.height }
            )
          }}
        />
      </div>
      {versionId && (
        <AnnotationOverlay
          versionId={versionId}
          layout={pinLayout}
          annotations={openPins}
          draft={draft}
          armed={commentMode}
          hoveredId={hoveredId}
          openId={openId}
          onHoverChange={setHoveredId}
          onPlaceDraft={setDraft}
          onOpenChange={setOpenId}
          onDismissDraft={() => setDraft(null)}
          onResolve={(id) => setResolved(id, true)}
        />
      )}
    </div>
  ) : (
    <FallbackPreviewCard
      blob={blob}
      snapshotUrl={shownSnapshotUrl}
      onImageError={() => setSnapshotBroken(true)}
      overlay={
        versionId &&
        shownSnapshotUrl && (
          <AnnotationOverlay
            versionId={versionId}
            layout={pinLayout}
            annotations={openPins}
            draft={draft}
            armed={commentMode}
            hoveredId={hoveredId}
            openId={openId}
            onHoverChange={setHoveredId}
            onPlaceDraft={setDraft}
            onOpenChange={setOpenId}
            onDismissDraft={() => setDraft(null)}
            onResolve={(id) => setResolved(id, true)}
          />
        )
      }
    />
  )

  return (
    <>
      <div css={styles.previewBody(isExpanded)} ref={measureRef}>
        {canPin && (
          <div css={styles.previewActions}>
            <Button
              buttonText={commentMode ? 'Done' : 'Add feedback'}
              name={commentMode ? 'Exit comment mode' : 'Add feedback'}
              variant={commentMode ? 'primary' : 'secondary'}
              icon={<Icon glyph="gem-comment" ariaHidden />}
              iconPosition={ButtonIconPosition.leading}
              onClick={() => {
                setCommentMode(!commentMode)
                setDraft(null)
              }}
            />
          </div>
        )}
        {shouldFitToWidth ? (
          // The frame renders at the post's own width, which may be wider than the
          // canvas until the scale lands (and taller than the scaled post after,
          // since a transform leaves the layout box at full size). Clip to the
          // canvas either way so the post never escapes it.
          <div
            css={styles.scaleClip}
            style={{
              blockSize:
                isScaled && dimensions ? dimensions.height * scale : undefined,
            }}
          >
            {frame}
          </div>
        ) : (
          frame
        )}
      </div>
      {version && (
        <VersionByline
          authorName={version.authorName}
          createdAt={version.createdAt}
          label={version.label}
          canEdit={canEdit}
          onLabelCommit={onLabelCommit}
        />
      )}
      {/* The details are the fields the preview itself can't show, so they follow
          the canvas everywhere it renders — including each compare pane, where
          they are what a reader is comparing. */}
      <VersionDetailsTable blob={blob} />
      {versionId && (
        <FeedbackCard
          annotations={openPins}
          resolvedAnnotations={resolvedPins}
          highlightedId={highlightedId}
          hoveredId={hoveredId}
          onHoverChange={setHoveredId}
          onSelect={selectFromList}
          onUnresolve={(id) => setResolved(id, false)}
        />
      )}
    </>
  )
}
